/**
 * The tenant admin's left menu, as agreed on 3 Oct (Documentation/ADMIN-NAV.md, Rodrigo + Abel), shown to the
 * tenant admin and, with the matrix's visibility, to the tenant Front Desk (specs/front-desk-tenant-permisos.md)
 * and the estate group roles (specs/estate-group-roles.md §4, SAM-1108).
 * No new screens: every entry is a route that already exists, renamed and regrouped. What the nav lists that has
 * no screen yet (Visitors, Estates settings, Notifications > Email / SMS) waits for the Users and Estates PRs.
 * SAM-1307: Support (canvas C5) sits after Settings and before Advanced; Estate Requests moved there from Users.
 */
import type { MenuItemData } from './useMenuBuilder';
import type { UserPermissions } from './useUserPermissions';
import { matrixRoleOf, type FdSection } from '../utils/frontDeskMatrix';

type EstateGroupFlags = Partial<Pick<UserPermissions, 'isEstateGroupAdmin' | 'isEstateGroupFrontDesk'>>;
type Perms = Pick<UserPermissions, 'isAdmin' | 'isTenantFrontDesk' | 'isXhinolaAdmin' | 'isTenantSettings' | 'isRolesManager'>
  & EstateGroupFlags;

/**
 * The tenant menu is for the tenant admin, the tenant Front Desk and the estate group roles (a subset of it);
 * every other role keeps its own menu.
 */
export const usesTenantNav = (p: Pick<UserPermissions, 'isAdmin' | 'isTenantFrontDesk'> & EstateGroupFlags): boolean =>
  p.isAdmin || matrixRoleOf(p) !== null;

export const buildTenantNav = (p: Perms, fdSees: (section: FdSection) => boolean): MenuItemData[] => {
  // The admin sees every section; a matrix role (tenant or estate group Front Desk, estate group admin) the ones
  // its matrix (or the server's grant) lets it view. The entries below gated on isAdmin stay the admin's only.
  const isMatrixRole = !p.isAdmin && matrixRoleOf(p) !== null;
  const sees = (section: FdSection): boolean => p.isAdmin || (isMatrixRole && fdSees(section));
  const nav: MenuItemData[] = [];

  if (sees('dashboard')) nav.push({ title: 'Dashboard', itemId: '#dashboard', iconName: 'LayoutDashboard' });

  // Passes: the Invitations screen under its new name.
  if (sees('passes')) nav.push({ title: 'Passes', itemId: '#invitations', iconName: 'UserPlus' });

  if (sees('estates')) nav.push({ title: 'Estates', itemId: '#estates', iconName: 'Home' });

  if (sees('users')) {
    nav.push({
      title: 'Users',
      itemId: '#users-group',
      iconName: 'User',
      subItems: [
        { title: 'Owners & Residents', itemId: '#users', iconName: 'Users' },
        // The tenant Admin's list of Property Managers: the Users table filtered by that user type. (/property-admin is the
        // Property Manager's own page; it is requirePropertyManager with no fdSection, so not for the Front Desk or the Admin.)
        ...(p.isAdmin ? [{ title: 'Property Managers', itemId: '#users?type=property_manager', iconName: 'Building2' }] : []),
      ],
    });
  }

  if (sees('bookings') || sees('business') || (p.isAdmin && sees('transactions'))) {
    const business: MenuItemData[] = [];
    // Activity (bookings and passes of every business) replaces Arrivals; the route stays /bookings.
    if (sees('bookings')) business.push({ title: 'Activity', itemId: '#bookings', iconName: 'CalendarDays' });
    if (sees('business')) {
      business.push({ title: 'List', itemId: '#business/list', iconName: 'List' });
      business.push({ title: 'Services', itemId: '#services', iconName: 'Briefcase' });
      business.push({ title: 'Products', itemId: '#products', iconName: 'ShoppingBag' });
    }
    // Transactions (Payments): never for the Front Desk.
    if (p.isAdmin) business.push({ title: 'Transactions', itemId: '#payments', iconName: 'CreditCard' });
    nav.push({ title: 'Business', itemId: '#business', iconName: 'Building2', subItems: business });
  }

  if (sees('events')) {
    nav.push({
      title: 'Events',
      itemId: '#eventsad',
      iconName: 'Calendar',
      subItems: [
        { title: 'List', itemId: '#eventsad/list', iconName: 'List' },
        // Access Settings is the access_settings table itself: the Front Desk may not list it (403, Logic), so it is
        // for the tenant admin and Xhinola only. The Front Desk sees the settings inside each event.
        ...(p.isAdmin ? [{ title: 'Access Settings', itemId: '#eventsad/access-settings', iconName: 'Settings' }] : []),
      ],
    });
  }

  if (sees('cms')) {
    nav.push({
      title: 'CMS',
      itemId: '#cms',
      iconName: 'AppWindow',
      subItems: [
        { title: 'Articles', itemId: '#settings/articles', iconName: 'FileText' },
        { title: 'Ads', itemId: '#settings/ads', iconName: 'Image' },
        { title: 'App', itemId: '#settings/home-screen', iconName: 'Smartphone' },
      ],
    });
  }

  if (sees('notifications')) {
    nav.push({
      title: 'Notifications',
      itemId: '#notifications',
      iconName: 'Bell',
      subItems: [
        { title: 'List', itemId: '#settings/push-notifications', iconName: 'List' },
        // Templates are for xhinola_admin only: not the tenant Admin (back with SAM-1265) and not the Front Desk (SAM-1312, U19). The route still opens.
        ...(p.isXhinolaAdmin ? [{ title: 'Templates', itemId: '#settings/notification-templates', iconName: 'FileText' }] : []),
      ],
    });
  }

  // Settings > Employees. The admin and front-desk accounts live in the Users screen's Admin Users tab and the
  // Gatekeepers (the gates' staff) have their own screen; both are for the tenant admin and Xhinola only, the
  // Front Desk gets neither (SAM-946, Rodrigo 15 Oct 2026: the matrix gives it nothing in `employees`).
  if (sees('employees')) {
    const employees: MenuItemData[] = [];
    if (p.isAdmin) employees.push({ title: 'Admin & Front desk', itemId: '#users?tab=admin-users', iconName: 'UserCog' });
    employees.push({ title: 'Gatekeepers', itemId: '#settings/gatekeeper-users', iconName: 'KeyRound' });
    nav.push({
      title: 'Settings',
      itemId: '#settings',
      iconName: 'Settings',
      subItems: [
        { title: 'Employees', itemId: '#employees', iconName: 'Users', subItems: employees },
        // SAM-946 (mock Gates v3): the tenant's external and internal gates, under Employees; admin and Xhinola only.
        { title: 'Gates', itemId: '#settings/gates', iconName: 'Landmark' },
      ],
    });
  }

  // Support (canvas C5): the requests the tenant answers. Estate Requests is the table that used to be Users > Requests, for
  // whoever sees Users (the Front Desk approves, the estate group roles read). Business Requests (SAM-1298): the admin and
  // Xhinola answer every type, the tenant Front Desk the carnets (Employee).
  if (sees('users')) {
    const support: MenuItemData[] = [{ title: 'Estate Requests', itemId: '#support/estate-requests', iconName: 'Inbox' }];
    if (p.isAdmin || p.isTenantFrontDesk) support.push({ title: 'Business Requests', itemId: '#support/business-requests', iconName: 'Briefcase' });
    nav.push({ title: 'Support', itemId: '#support', iconName: 'LifeBuoy', subItems: support });
  }

  // Advanced: the Xhinola team only (and whoever holds the tenant-settings / roles groups for those entries).
  const advanced: MenuItemData[] = [];
  if (p.isXhinolaAdmin || p.isTenantSettings) advanced.push({ title: 'Tenant Settings', itemId: '#settings/tenant', iconName: 'Settings' });
  if (p.isXhinolaAdmin || p.isRolesManager) {
    advanced.push({ title: 'Roles', itemId: '#settings/roles', iconName: 'ShieldCheck' });
    advanced.push({ title: 'Pass types', itemId: '#settings/invitation-types', iconName: 'Tag' });
  }
  if (p.isXhinolaAdmin) {
    advanced.push({ title: 'Permissions', itemId: '#settings/permissions', iconName: 'ShieldCheck' });
    advanced.push({ title: 'Merchant Configuration', itemId: '#settings/merchant', iconName: 'CreditCard' });
  }
  if (advanced.length > 0) nav.push({ title: 'Advanced', itemId: '#advanced', iconName: 'Shield', subItems: advanced });

  return nav;
};
